import React, { useState } from "react";
import { Anchor, Stack } from "@mantine/core";
import RadioRingRow from "./RadioRingRow";
import { PaymentTerminal } from "../../../types/paymentTerminal";

interface TerminalRevealProps {
  /** The location's attached terminals (1-3). */
  terminals: PaymentTerminal[];
  /** Currently chosen reader device id, or null when a pick is still needed. */
  selectedTerminalDeviceId: string | null;
  onSelectTerminal: (deviceId: string) => void;
}

const terminalLabel = (terminal: PaymentTerminal): string =>
  terminal.label ||
  `Terminal ····${terminal.stripe_terminal_device_id.slice(-4)}`;

/**
 * Card Present reveal. One terminal auto-selects (single row). With several,
 * the chosen terminal shows with a "Change" control; when nothing is chosen
 * yet, every terminal is offered as a radio row so staff pick which reader to
 * charge on. Tip/splash config is shared across all terminals, so only the
 * reader identity varies here.
 */
export default function TerminalReveal({
  terminals,
  selectedTerminalDeviceId,
  onSelectTerminal,
}: TerminalRevealProps) {
  const [isChanging, setIsChanging] = useState(false);

  const selected = terminals.find(
    (t) => t.stripe_terminal_device_id === selectedTerminalDeviceId,
  );
  const multiple = terminals.length > 1;
  const showPicker = multiple && (!selected || isChanging);

  if (showPicker) {
    return (
      <Stack gap="xs">
        {terminals.map((terminal) => (
          <RadioRingRow
            key={terminal.id}
            selected={
              terminal.stripe_terminal_device_id === selectedTerminalDeviceId
            }
            onSelect={() => {
              onSelectTerminal(terminal.stripe_terminal_device_id);
              setIsChanging(false);
            }}
            title={terminalLabel(terminal)}
          />
        ))}
      </Stack>
    );
  }

  // Single terminal, or a chosen terminal with a "Change" affordance. The
  // Change control is a SIBLING of the row (not nested inside its button) to
  // keep the markup valid and accessible.
  const row = selected ?? terminals[0];
  if (!row) return null;

  return (
    <Stack gap={4}>
      <RadioRingRow selected onSelect={() => {}} title={terminalLabel(row)} />
      {multiple && (
        <Anchor
          component="button"
          type="button"
          size="sm"
          c="var(--brand-teal)"
          onClick={() => setIsChanging(true)}
          style={{ alignSelf: "flex-start" }}
        >
          Change terminal
        </Anchor>
      )}
    </Stack>
  );
}
